import { type FC, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import PhotoArrows from 'dibs-interiors-photos/exports/PhotoArrows.tsx';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { usePhotosLikeDataRefetch } from 'dibs-interiors-photos/exports/usePhotosLikeDataRefetch.ts';
import { SbSharedInteriorsHeader } from '../SbSharedInteriorsModule/SbSharedInteriorsHeader.tsx';
import { SbSharedInteriorsBrowseMore } from '../SbSharedInteriorsModule/SbSharedInteriorsBrowseMore.tsx';
import { SbRespInteriorsLazyLoadImage } from './SbRespInteriorsLazyLoadImage.tsx';
import { getPhotoIndex } from '../SbSharedInteriorsModule/interiorsModuleHelpers.ts';
import SbSharedInteriorsHeartWrapper from '../SbSharedInteriorsModule/SbSharedInteriorsHeartWrapper/SbSharedInteriorsHeartWrapper.tsx';
import SbSharedInteriorsPhotoRelatedSearchLinks from '../SbSharedInteriorsModule/SbSharedInteriorsPhotoRelatedSearchLinks.tsx';

import { type SbRespInteriorsPhotoGallery_interiorPhotos$data } from './__generated__/SbRespInteriorsPhotoGallery_interiorPhotos.graphql.ts';
import { type SbRespInteriorsPhotoGallery_itemSearch$data } from './__generated__/SbRespInteriorsPhotoGallery_itemSearch.graphql.ts';
import { type SbRespInteriorsPhotoGallery_user$data } from './__generated__/SbRespInteriorsPhotoGallery_user.graphql.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbRespInteriorsPhotoGallery.scss';

type Props = {
    interiorPhotos: SbRespInteriorsPhotoGallery_interiorPhotos$data | null | undefined;
    itemSearch: SbRespInteriorsPhotoGallery_itemSearch$data;
    photoIndex: number;
    onPhotoIndexChange: (newIndex: number) => void;
    onPhotoClick: () => void;
    isClient: boolean;
    onPhotoLikeAdded: () => void;
    user: SbRespInteriorsPhotoGallery_user$data;
    isModuleVisible: boolean;
};

const SbRespInteriorsPhotoGalleryComponent: FC<Props> = ({
    interiorPhotos,
    itemSearch,
    photoIndex,
    onPhotoIndexChange,
    onPhotoClick,
    isClient,
    onPhotoLikeAdded,
    user,
    isModuleVisible,
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);

    const photos = interiorPhotos?.photos?.filter(filterNulls) || [];
    // +1 b/c showing browse screen at the end
    const photoCount = photos.length + 1;
    const lastPhotoIndex = photoCount - 1;
    const handlePageChange = (nextIndex: number): void => {
        onPhotoIndexChange(getPhotoIndex(nextIndex, lastPhotoIndex));
    };

    const isLastPage = photoIndex === lastPhotoIndex;
    const photo = photos[photoIndex];
    const { photoLikeDataMap, isRefetching } = usePhotosLikeDataRefetch({
        photosRef: photos,
        shouldFetch: isModuleVisible,
        userId: user?.serviceId || '',
    });
    const { photoLikeId } = photoLikeDataMap.get(photo?.serviceId || '') || {};

    if (photos.length === 0) {
        return null;
    }

    return (
        <div
            className={classnames(
                styles.wrapper,
                dibsCss.mrXxlarge,
                dibsCss.prXxlarge,
                dibsCss.grow,
                dibsCss.shrink0,
                dibsCss.basis60,
                dibsCss.borderR,
                dibsCss.borderRSolid,
                dibsCss.borderRDolphin
            )}
        >
            <div className={classnames(styles.imgWrapper, dibsCss.relative, dibsCss.bgWhite)}>
                {isLastPage ? (
                    <div className={classnames(dibsCss.absolute, dibsCss.wFull, dibsCss.hFull)}>
                        <SbSharedInteriorsBrowseMore interiorPhotos={interiorPhotos} />
                    </div>
                ) : (
                    <SbRespInteriorsLazyLoadImage
                        imgClass={classnames(
                            styles.img,
                            dibsCss.absolute,
                            dibsCss.top50,
                            dibsCss.left50,
                            dibsCss.maxWFull,
                            dibsCss.maxHFull
                        )}
                        photo={photo}
                        onImageLoaded={() => setImageLoaded(true)}
                    />
                )}
                {photoCount > 0 && imageLoaded && (
                    <div
                        className={classnames(dibsCss.wFull, dibsCss.hFull, dibsCss.cursorPointer)}
                        onClick={onPhotoClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                onPhotoClick();
                            }
                        }}
                    >
                        <div
                            className={classnames(
                                styles.overlay,
                                dibsCss.opacity0,
                                dibsCss.absolute,
                                dibsCss.inset0,
                                dibsCss.flex,
                                dibsCss.justifyCenter,
                                dibsCss.itemsCenter
                            )}
                        >
                            {!isLastPage && !isRefetching && isClient && (
                                <SbSharedInteriorsHeartWrapper
                                    onPhotoLikeAdded={onPhotoLikeAdded}
                                    photoLikeId={photoLikeId}
                                    photo={photo}
                                />
                            )}
                            <PhotoArrows
                                hideNext={isLastPage}
                                hidePrev={photoIndex === 0}
                                onNextClick={event => {
                                    event.stopPropagation();
                                    handlePageChange(photoIndex + 1);
                                }}
                                onPrevClick={event => {
                                    event.stopPropagation();
                                    handlePageChange(photoIndex - 1);
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className={classnames(dibsCss.mtSmall, dibsCss.mbLarge)}>
                <SbSharedInteriorsHeader itemSearch={itemSearch} />
            </div>
            <div className={classnames(dibsCss.flex, dibsCss.flexWrap)}>
                <SbSharedInteriorsPhotoRelatedSearchLinks
                    isPillBackgroundWhite
                    interiorPhotos={interiorPhotos}
                />
            </div>
        </div>
    );
};

export const SbRespInteriorsPhotoGallery = createFragmentContainer(
    SbRespInteriorsPhotoGalleryComponent,
    {
        itemSearch: graphql`
            fragment SbRespInteriorsPhotoGallery_itemSearch on ItemSearchQueryConnection {
                ...SbSharedInteriorsHeader_itemSearch
            }
        `,
        interiorPhotos: graphql`
            fragment SbRespInteriorsPhotoGallery_interiorPhotos on SearchBrowseInteriorPhotosModule {
                ...SbSharedInteriorsBrowseMore_interiorPhotos
                ...interiorsModuleHelpers_interiorPhotos
                ...SbSharedInteriorsPhotoRelatedSearchLinks_interiorPhotos
                photos {
                    id
                    path
                    name
                    url
                    serviceId
                    ...usePhotosLikeDataRefetch_photos
                    ...SbSharedInteriorsHeartWrapper_photo
                }
            }
        `,
        user: graphql`
            fragment SbRespInteriorsPhotoGallery_user on User {
                serviceId
            }
        `,
    }
);
